import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Zap, Send, ArrowRight, Loader2, User, Mail, CheckCircle, BarChart3, Target, Sparkles, Share2, Linkedin, Twitter, Copy, Check, Clock, Gift, Quote } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://boost-strategia.com';
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_65659477-73eb-427c-9057-d93b7f82d27e/artifacts/2a0treyn_vignette-score-strat%C3%A9tique.jpg';

// Timer promo
const PROMO_CODE = 'BIENVENUE20';
const TIMER_DURATION = 48 * 60 * 60 * 1000; // 48h
const STORAGE_KEY = 'boost_promo_timer_start';

// Contenu personnalisé par profil
const PROFILE_CONTENT = {
  'Plafonneur': {
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    title: "Ton score révèle quelque chose d'important…",
    subtitle: "Tu avances à l'instinct. Et c'est exactement ce qui te coûte des ventes.",
    body: [
      "Ton Score de Scalabilité™ montre que les bases ne sont pas encore posées. Pas de panique — c'est récupérable. Mais ça ne se récupère pas au hasard.",
      "Ce qui bloque ton CA, ce n'est pas ton offre en elle-même. C'est que ton client idéal n'est pas encore défini avec suffisamment de précision pour que ton message atterrisse juste… et que tes ventes deviennent prévisibles.",
      "Boost-StrategIA est conçu exactement pour ça. Il te guide pas à pas pour poser les bonnes bases — dans le bon ordre — sans te noyer dans la théorie."
    ],
    actionPlan: {
      title: "Tes 3 premières actions prioritaires",
      steps: [
        { title: "Définis ton client idéal avec précision", description: "Avant de vendre, tu dois savoir exactement à qui tu t'adresses. Un avatar flou = un message qui ne touche personne." },
        { title: "Clarifie ta promesse principale", description: "Quelle transformation concrète apportes-tu ? Si tu ne peux pas l'expliquer en une phrase, ton prospect ne comprendra pas non plus." },
        { title: "Structure une offre lisible", description: "Prix, livrables, durée, résultat attendu. Tout doit être clair avant de communiquer." }
      ]
    }
  },
  'Dispersé': {
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    title: "Tu as des bases… mais elles ne travaillent pas ensemble.",
    subtitle: "Ton offre dit une chose. Ton message en dit une autre. Ton plan marketing ? Il n'existe pas vraiment encore.",
    body: [
      "Ton Score de Scalabilité™ le confirme : tu n'es pas au point zéro. Tu as déjà des clients, une offre, de l'expérience.",
      "Mais quelque chose reste incohérent entre ce que tu proposes, ce que tu communiques, et ce que tu cibles. Et cette incohérence, c'est elle qui rend ton CA imprévisible d'un mois à l'autre.",
      "Boost-StrategIA t'aide à remettre tout ça en alignement. Et à construire un plan marketing sur 30 à 90 jours qui tient vraiment la route."
    ],
    actionPlan: {
      title: "Tes 3 premières actions prioritaires",
      steps: [
        { title: "Aligne ton message sur ton offre", description: "Vérifie que ce que tu dis sur les réseaux correspond vraiment à ce que tu vends. Incohérence = confusion = pas de vente." },
        { title: "Choisis UN canal d'acquisition principal", description: "Être partout = être nulle part. Concentre 80% de ton énergie sur le canal qui fonctionne le mieux pour toi." },
        { title: "Crée un plan marketing 30 jours", description: "Pas de plan = navigation à vue. Définis tes actions semaine par semaine avec des objectifs mesurables." }
      ]
    }
  },
  'Prédictible': {
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    title: "Tu as une structure. C'est rare. Maintenant, il s'agit de passer à l'étape d'après.",
    subtitle: "Optimiser. Automatiser. Scaler — sans te disperser.",
    body: [
      "Ton Score de Scalabilité™ le confirme : tu es déjà dans le bon couloir. Mais avoir une structure, ce n'est pas la même chose que l'exploiter à 100 %.",
      "Il reste probablement des leviers que tu n'as pas encore activés — sur ton message, ta différenciation, ou ton plan à 90 jours.",
      "Boost-StrategIA t'aide à les identifier avec précision… et à structurer ta croissance de façon intelligente, sans t'éparpiller."
    ],
    actionPlan: {
      title: "Tes 3 prochains leviers de croissance",
      steps: [
        { title: "Optimise ton taux de conversion", description: "Tu as du trafic, mais convertis-tu assez ? Analyse ton tunnel et identifie les points de friction." },
        { title: "Systématise ce qui fonctionne", description: "Tes meilleures ventes suivent un pattern. Documente-le et transforme-le en process répétable." },
        { title: "Prépare ton plan à 90 jours", description: "Avec une base solide, tu peux voir plus loin. Définis tes objectifs de scaling et les ressources nécessaires." }
      ]
    },
    testimonials: [
      { quote: "J'ai pris une claque. Le GPT m'a aidé à clarifier mon offre, mon positionnement et à identifier mes angles morts en 30 minutes.", author: "Alexis Rocco", role: "Growth Marketer" },
      { quote: "Après 6 ans et demi avec plusieurs spécialistes marketing, je voulais stabiliser mon CA. Les résultats ? +83% et +124% sur nos deux premiers lancements.", author: "Coach et préparateur mental", role: "15 ans d'expérience" }
    ]
  }
};

const Diagnostic = () => {
  const [step, setStep] = useState('capture'); // 'capture' | 'chat' | 'complete'
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState({ score: null, profile: null });
  const [promoTimeLeft, setPromoTimeLeft] = useState(null);
  const [isPromoExpired, setIsPromoExpired] = useState(true);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Timer promo (activé quand le test est terminé)
  useEffect(() => {
    if (step !== 'complete') return;
    
    const startTime = localStorage.getItem(STORAGE_KEY);
    if (!startTime) return;

    const updateTimer = () => {
      const elapsed = Date.now() - parseInt(startTime);
      const remaining = TIMER_DURATION - elapsed;
      if (remaining <= 0) {
        setIsPromoExpired(true);
        setPromoTimeLeft(null);
      } else {
        setIsPromoExpired(false);
        setPromoTimeLeft(remaining);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [step]);

  // Format time helper
  const formatTime = (ms) => {
    if (!ms) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Check for existing session in localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('diagnostic_session');
    if (savedSession) {
      const session = JSON.parse(savedSession);
      // Optionally restore session
      // setSessionId(session.sessionId);
      // setStep('chat');
    }
  }, []);

  const handleStartDiagnostic = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim() || !email.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/diagnostic/start`, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim()
      });

      const { session_id, message } = response.data;
      
      setSessionId(session_id);
      setMessages([{ role: 'assistant', content: message }]);
      setStep('chat');
      
      // Save session to localStorage
      localStorage.setItem('diagnostic_session', JSON.stringify({
        sessionId: session_id,
        email: email.trim().toLowerCase(),
        firstName: firstName.trim()
      }));
      
      // Sauvegarder aussi pour pré-remplir le formulaire d'inscription
      localStorage.setItem('diagnostic_user', JSON.stringify({
        email: email.trim().toLowerCase(),
        name: firstName.trim()
      }));
      
    } catch (error) {
      console.error('Error starting diagnostic:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage du test');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      const response = await axios.post(`${API}/diagnostic/chat`, {
        session_id: sessionId,
        message: userMessage
      });

      const { response: aiResponse, is_complete, score, profile } = response.data;
      
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);

      if (is_complete) {
        setResult({ score, profile });
        setStep('complete');
        localStorage.removeItem('diagnostic_session');
        
        // Initialiser le timer promo si pas encore fait
        if (!localStorage.getItem('boost_promo_timer_start')) {
          localStorage.setItem('boost_promo_timer_start', Date.now().toString());
        }
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      // Re-add the message to input if failed
      setInputMessage(userMessage);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const getProfileColor = (profile) => {
    switch (profile?.toLowerCase()) {
      case 'plafonneur': return 'text-red-400';
      case 'dispersé': return 'text-yellow-400';
      case 'prédictible': return 'text-green-400';
      default: return 'text-primary';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags for Social Sharing */}
      <Helmet>
        <title>Score de Scalabilité™ | Boost-StrategIA</title>
        <meta name="description" content="Découvrez votre Score de Scalabilité™ et comprenez ce qui bloque la croissance de votre business. Test gratuit en 10 questions." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/diagnostic`} />
        <meta property="og:title" content="Toi aussi, découvre ton Score de Scalabilité™ !" />
        <meta property="og:description" content="Découvre si ton business est réellement prêt à croître. Test gratuit en 10 questions." />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:site_name" content="Boost-StrategIA" />
        <meta property="og:locale" content="fr_FR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/diagnostic`} />
        <meta name="twitter:title" content="Toi aussi, découvre ton Score de Scalabilité™ !" />
        <meta name="twitter:description" content="Découvre si ton business est réellement prêt à croître. Test gratuit en 10 questions." />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
            </div>
            <span className="text-lg sm:text-2xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/pricing" className="text-sm text-muted hover:text-foreground transition-colors">
              Tarifs
            </Link>
            <Link to="/auth">
              <Button variant="outline" size="sm">Connexion</Button>
            </Link>
          </nav>
        </div>
      </header>

      <div className="pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          
          {/* Step: Capture */}
          <AnimatePresence mode="wait">
            {step === 'capture' && (
              <motion.div
                key="capture"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                <div className="mb-8">
                  <motion.div 
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-pink-500/20 border border-primary/30 rounded-full px-4 py-2 mb-4"
                  >
                    <BarChart3 className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Test gratuit • 10 minutes</span>
                  </motion.div>
                  
                  <h1 className="text-3xl md:text-5xl font-heading font-bold mb-4">
                    Quel est le <span className="text-primary">Score de Scalabilité™</span><br />de votre business ?
                  </h1>
                  
                  <p className="text-lg text-muted max-w-xl mx-auto">
                    Découvrez si votre business est réellement prêt à croître.
                  </p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-card border border-border rounded-2xl p-8 max-w-md mx-auto"
                >
                  <form onSubmit={handleStartDiagnostic} className="space-y-4">
                    <div>
                      <label className="text-sm text-muted block mb-2 text-left">Votre prénom</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
                        <Input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Marie"
                          className="pl-10"
                          required
                          data-testid="diagnostic-firstname"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm text-muted block mb-2 text-left">Votre email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="marie@example.com"
                          className="pl-10"
                          required
                          data-testid="diagnostic-email"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full shadow-glow-md mt-6" 
                      size="lg"
                      disabled={isLoading}
                      data-testid="diagnostic-start-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Démarrage...
                        </>
                      ) : (
                        <>
                          Faire le test
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-xs text-muted mt-4">
                    En continuant, vous acceptez de recevoir des emails de Boost-StrategIA.
                    Vous pouvez vous désinscrire à tout moment.
                  </p>
                </motion.div>

                {/* What you'll get */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-12 grid grid-cols-3 gap-4 max-w-lg mx-auto"
                >
                  {[
                    { icon: <Target className="w-5 h-5" />, text: 'Score sur 100' },
                    { icon: <BarChart3 className="w-5 h-5" />, text: 'Profil stratégique' },
                    { icon: <Sparkles className="w-5 h-5" />, text: 'Plan d\'action' }
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary mx-auto mb-2">
                        {item.icon}
                      </div>
                      <span className="text-xs text-muted">{item.text}</span>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            )}

            {/* Step: Chat */}
            {step === 'chat' && (
              <motion.div
                key="chat"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-[calc(100vh-180px)] flex flex-col"
              >
                <div className="text-center mb-4">
                  <h2 className="text-xl font-heading font-semibold">
                    Test Stratégique de Business
                  </h2>
                  <p className="text-sm text-muted">Répondez librement aux questions</p>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto bg-card/30 border border-border rounded-xl p-4 mb-4">
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-primary text-black'
                              : 'bg-card border border-border'
                          }`}
                        >
                          {msg.role === 'assistant' ? (
                            <div className="prose prose-invert prose-sm max-w-none diagnostic-content">
                              <ReactMarkdown
                                components={{
                                  // Titres h2 en rose
                                  h2: ({node, children, ...props}) => (
                                    <h2 className="text-pink-400 font-bold text-lg mt-4 mb-2" {...props}>{children}</h2>
                                  ),
                                  // Titres h3 en rose
                                  h3: ({node, children, ...props}) => (
                                    <h3 className="text-pink-400 font-semibold text-base mt-3 mb-2" {...props}>{children}</h3>
                                  ),
                                  // Titres h4 en bleu (sous-titres)
                                  h4: ({node, children, ...props}) => (
                                    <h4 className="text-primary font-semibold text-sm mt-2 mb-1" {...props}>{children}</h4>
                                  ),
                                  // Texte en gras
                                  strong: ({node, children, ...props}) => {
                                    const text = String(children);
                                    // Feedback en rose
                                    if (text.includes('Mon feedback') || text.includes('feedback')) {
                                      return <strong className="text-pink-400" {...props}>{children}</strong>;
                                    }
                                    // Titres de sections en rose
                                    if (text.includes('Score de Scalabilité') || 
                                        text.includes('Prochaine étape') || 
                                        text.includes('Ce que ton score révèle') ||
                                        text.includes('Mini-plan d\'action') ||
                                        text.includes('Plan d\'action') ||
                                        text.includes('Profil :')) {
                                      return <strong className="text-pink-400 block mt-3" {...props}>{children}</strong>;
                                    }
                                    // Sous-titres numérotés en bleu
                                    if (text.match(/^\d+\.\s/) || 
                                        text.includes('Offre') || 
                                        text.includes('Message') || 
                                        text.includes('Pilotage') ||
                                        text.includes('clarifier') ||
                                        text.includes('Question')) {
                                      return <strong className="text-primary" {...props}>{children}</strong>;
                                    }
                                    return <strong {...props}>{children}</strong>;
                                  },
                                  // Liens en rose
                                  a: ({node, children, href, ...props}) => {
                                    const text = String(children);
                                    if (text.includes('Boost-StrategIA') || text.includes('offres')) {
                                      return <a href={href} className="text-pink-400 hover:text-pink-300 underline font-semibold" {...props}>{children}</a>;
                                    }
                                    return <a href={href} className="text-primary hover:text-primary/80 underline" {...props}>{children}</a>;
                                  },
                                  // Paragraphes avec Boost-StrategIA en rose
                                  p: ({node, children, ...props}) => {
                                    const text = String(children);
                                    if (text.includes('👉') && text.includes('Boost-StrategIA')) {
                                      return (
                                        <p {...props}>
                                          {String(children).split('Boost-StrategIA').map((part, i, arr) => (
                                            i < arr.length - 1 ? (
                                              <span key={i}>{part}<span className="text-pink-400 font-semibold">Boost-StrategIA</span></span>
                                            ) : part
                                          ))}
                                        </p>
                                      );
                                    }
                                    return <p {...props}>{children}</p>;
                                  }
                                }}
                              >{msg.content}</ReactMarkdown>
                            </div>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                    
                    {isSending && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-start"
                      >
                        <div className="bg-card border border-border rounded-2xl px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                            <span className="text-sm text-muted">En train d'analyser...</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Tapez votre réponse..."
                    disabled={isSending}
                    className="flex-1"
                    data-testid="diagnostic-chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={isSending || !inputMessage.trim()}
                    className="shadow-glow-sm"
                    data-testid="diagnostic-send-btn"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </form>
              </motion.div>
            )}

            {/* Step: Complete - Résultats + Contenu personnalisé */}
            {step === 'complete' && (
              <motion.div
                key="complete"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-3xl mx-auto"
              >
                {/* Score en grand */}
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.2 }}
                    className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle className="w-10 h-10 text-primary" />
                  </motion.div>
                  
                  {result.score !== null && (
                    <div className="mt-4">
                      <p className="text-muted mb-2">Ton Score de Scalabilité™</p>
                      <p className={`text-6xl font-bold ${getScoreColor(result.score)}`}>
                        {result.score}<span className="text-3xl text-muted">/100</span>
                      </p>
                      {result.profile && (
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mt-4 ${PROFILE_CONTENT[result.profile]?.bgColor || 'bg-primary/10'} ${PROFILE_CONTENT[result.profile]?.borderColor || 'border-primary/30'} border`}>
                          <span className={`text-lg font-medium ${PROFILE_CONTENT[result.profile]?.color || 'text-primary'}`}>
                            Profil : {result.profile}
                          </span>
                        </div>
                      )}
                      
                      {/* Bouton télécharger le PDF */}
                      {sessionId && (
                        <div className="mt-6">
                          <a
                            href={`${API}/diagnostic/download-pdf/${sessionId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg text-primary font-medium transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Télécharger mon rapport PDF
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Offre promo avec timer - EN HAUT */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-card border border-primary/30 rounded-2xl p-6 sm:p-8 mb-10"
                >
                  {!isPromoExpired && promoTimeLeft ? (
                    <>
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Gift className="w-6 h-6 text-primary" />
                        <h3 className="text-lg sm:text-xl font-bold">
                          Offre de bienvenue — réservée aux nouveaux membres
                        </h3>
                      </div>

                      <p className="text-center text-muted mb-4">
                        Utilise le code <span className="text-primary font-bold text-xl">{PROMO_CODE}</span> pour bénéficier de <span className="text-primary font-bold">-20 %</span> sur ton accès à Boost-StrategIA.
                      </p>

                      <div className="flex items-center justify-center gap-2 text-orange-400 mb-6">
                        <Clock className="w-5 h-5" />
                        <span className="font-medium">
                          Ce code expire dans {formatTime(promoTimeLeft)}
                        </span>
                      </div>

                      <div className="text-center">
                        <Link to="/pricing" state={{ fromThankYou: true, promoCode: PROMO_CODE }}>
                          <Button size="lg" className="text-lg px-8 py-6 shadow-glow-lg">
                            Je rejoins Boost-StrategIA avec -20 %
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Button>
                        </Link>
                        <p className="text-sm text-muted mt-3">
                          (Tu peux annuler à tout moment. Aucun engagement.)
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <p className="text-muted mb-6">
                        Découvre comment Boost-StrategIA peut t'aider à débloquer ta croissance.
                      </p>
                      <Link to="/pricing">
                        <Button size="lg" className="text-lg px-8 py-6">
                          Découvrir les offres
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </motion.div>

                {/* Contenu personnalisé selon le profil */}
                {result.profile && PROFILE_CONTENT[result.profile] && (
                  <>
                    {/* Titre et sous-titre du profil */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-center mb-8"
                    >
                      <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-3">
                        {PROFILE_CONTENT[result.profile].title}
                      </h2>
                      <p className={`text-lg ${PROFILE_CONTENT[result.profile].color} font-medium`}>
                        {PROFILE_CONTENT[result.profile].subtitle}
                      </p>
                    </motion.div>

                    {/* Corps du texte */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="space-y-4 mb-10 text-muted text-base"
                    >
                      {PROFILE_CONTENT[result.profile].body.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </motion.div>

                    {/* Plan d'action */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className={`${PROFILE_CONTENT[result.profile].bgColor} border ${PROFILE_CONTENT[result.profile].borderColor} rounded-2xl p-6 mb-10`}
                    >
                      <h3 className={`text-xl font-bold mb-6 ${PROFILE_CONTENT[result.profile].color}`}>
                        {PROFILE_CONTENT[result.profile].actionPlan.title}
                      </h3>
                      <div className="space-y-5">
                        {PROFILE_CONTENT[result.profile].actionPlan.steps.map((step, index) => (
                          <div key={index} className="flex gap-4">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full ${PROFILE_CONTENT[result.profile].bgColor} ${PROFILE_CONTENT[result.profile].borderColor} border flex items-center justify-center`}>
                              <span className={`font-bold text-sm ${PROFILE_CONTENT[result.profile].color}`}>{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-1">{step.title}</h4>
                              <p className="text-muted text-sm">{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}

                {/* Témoignages (seulement pour Prédictible) */}
                {result.profile === 'Prédictible' && PROFILE_CONTENT['Prédictible'].testimonials && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="mb-10"
                  >
                    <h3 className="text-xl font-bold text-center mb-6">
                      Ce qu'ils ont vécu. Dans leurs mots.
                    </h3>
                    <div className="space-y-4">
                      {PROFILE_CONTENT['Prédictible'].testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-card/50 border border-border rounded-xl p-6">
                          <Quote className="w-8 h-8 text-primary/30 mb-3" />
                          <p className="text-muted italic mb-4">"{testimonial.quote}"</p>
                          <div>
                            <p className="font-semibold">{testimonial.author}</p>
                            <p className="text-sm text-muted">{testimonial.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Boutons de partage */}
                <ShareButtons score={result.score} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Composant pour les boutons de partage
const ShareButtons = ({ score }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = `${SITE_URL}/diagnostic`;
  const shareText = `Je viens de tester le Score de Scalabilité™ de mon business et d'obtenir mes 3 premières actions à mettre en place. Mon score est de ${score}/100 ! Et toi, tu en es où ?`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Impossible de copier le lien');
    }
  };
  
  const shareOnLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };
  
  const shareOnX = () => {
    const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(xUrl, '_blank', 'width=600,height=400');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-card border border-border rounded-xl p-6 h-full flex flex-col justify-between"
    >
      <div>
        <div className="flex items-center justify-center gap-2 mb-4">
          <Share2 className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Partager mon score</h3>
        </div>
        
        <p className="text-sm text-muted mb-4 text-center">
          Je viens de tester le Score de Scalabilité™ de mon business. Mon score est de <span className="text-primary font-bold">{score}/100</span> !
        </p>
      </div>
      
      <div className="flex justify-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={shareOnLinkedIn}
          className="flex items-center gap-2"
          data-testid="share-linkedin"
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={shareOnX}
          className="flex items-center gap-2"
          data-testid="share-x"
        >
          <Twitter className="w-4 h-4" />
          X
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className="flex items-center gap-2"
          data-testid="share-copy"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copié !' : 'Copier'}
        </Button>
      </div>
    </motion.div>
  );
};

export default Diagnostic;
